import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import {
    createRelationships,
    fetchContactRelationshipById,
    updateContactRelationshipById,
} from 'api/relationships';
import {
    SelectContactForm,
    SelectContactFormValues,
} from 'assets/js/pages/contacts/SelectContactForm';
import { UseFormRelationshipPicker } from 'assets/js/pages/contacts/UseFormRelationshipPicker';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { parseInt } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactRelationshipFormValues extends SelectContactFormValues {
    relationToContact: string;
    relationFromContact: string;
}

const ContactRelationshipFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();
    const { relationship_id } = useParams<{ relationship_id?: string }>();
    const relationshipId = relationship_id ? parseInt(relationship_id) : undefined;
    const isEdit = !!relationshipId;

    const { data: contactRelationship } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactRelationshipById(
            accountId,
            contactId,
            relationshipId as number,
        ),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchContactRelationshipById(accountId, contactId, relationshipId as number),
                isEdit,
            ),
    });

    const relatedContact = contactRelationship?.related_contact;

    const methods = useGivebutterForm<ContactRelationshipFormValues>({
        defaultValues: {
            selectedContact: relatedContact
                ? [
                      {
                          id: relatedContact.id.toString(),
                          label: `${relatedContact.first_name} ${relatedContact.last_name}${
                              relatedContact.primary_email
                                  ? ` - ${relatedContact.primary_email}`
                                  : ''
                          }`,
                          value: relatedContact,
                      },
                  ]
                : [],
            relationToContact: isEdit && contactRelationship ? contactRelationship.type : '',
            relationFromContact:
                isEdit && contactRelationship ? contactRelationship.related_type : '',
        },
    });

    const { handleSubmit, resetFormButton, path, watch, setValue } = methods;

    const rawSelectedContact = watch('selectedContact');
    const selectedContact = rawSelectedContact[0] ? rawSelectedContact[0] : undefined;

    const { mutate: submit } = useMutation({
        mutationFn: async (formValues: ContactRelationshipFormValues) => {
            const { relationToContact, relationFromContact } = formValues;

            if (isEdit) {
                return updateContactRelationshipById(
                    accountId,
                    contactId,
                    contactRelationship!.related_id,
                    { related_type: relationToContact, type: relationFromContact },
                );
            }

            return createRelationships(accountId, [
                {
                    type: relationToContact,
                    relatedContactId: parseInt(selectedContact!.id),
                    contactId: contactId,
                },
                {
                    type: relationFromContact,
                    relatedContactId: contactId,
                    contactId: parseInt(selectedContact!.id),
                },
            ]);
        },
        onSuccess: async () => {
            toast.success(`${isEdit ? 'Updated' : 'Created'} relationship successfully!`);
            await queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(
                e.response?.data.message ??
                    `Failed to ${isEdit ? 'update' : 'create'} relationship. Please try again.`,
            );
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactRelationshipFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>{isEdit ? 'Edit' : 'Create'} Relationship</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        {!isEdit && (
                            <Form.Container>
                                <Label htmlFor={'contact-search'} className={'text-sm font-medium'}>
                                    Search for contact
                                </Label>
                                {selectedContact?.value ? (
                                    <div className={'flex items-center gap-2'}>
                                        <p>
                                            {selectedContact.value.first_name}{' '}
                                            {selectedContact.value.last_name}
                                        </p>
                                        <Button
                                            variant="none"
                                            color="primary"
                                            className="font-medium"
                                            onClick={() => {
                                                setValue('selectedContact', []);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Container>
                                        <SelectContactForm />
                                    </Form.Container>
                                )}
                            </Form.Container>
                        )}
                        {selectedContact?.value && (
                            <Box className={'bg-gray-25 p-4'}>
                                <UseFormRelationshipPicker
                                    contact={selectedContact.value}
                                    relationToPath={path('relationToContact')}
                                    relationFromPath={path('relationFromContact')}
                                    required
                                />
                            </Box>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton>
                                {isEdit ? 'Update' : 'Create'} Relationship
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { ContactRelationshipFormDrawer };
